'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { useQuery } from '@tanstack/react-query';

const HEADER_CATEGORY_CHILDREN_STALE_TIME = 30 * 60 * 1000;
const HEADER_CATEGORY_CHILDREN_GC_TIME = 6 * 60 * 60 * 1000;

type HeaderCategoryChildrenResponse = {
  data?: CategoriesEntity[];
};

export function useHeaderCategoryChildren({
  locale,
  parentId,
  enabled = true,
}: {
  locale: string;
  parentId?: CategoriesEntity['id'];
  enabled?: boolean;
}) {
  const normalizedParentId = parentId == null ? '' : String(parentId);

  return useQuery({
    queryKey: ['mediatip-header-category-children', locale, normalizedParentId],
    enabled: enabled && normalizedParentId.length > 0,
    staleTime: HEADER_CATEGORY_CHILDREN_STALE_TIME,
    gcTime: HEADER_CATEGORY_CHILDREN_GC_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async ({ signal }) => {
      const searchParams = new URLSearchParams({
        locale,
        parentId: normalizedParentId,
      });
      const response = await fetch(`/api/header-categories?${searchParams.toString()}`, {
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!response.ok) throw new Error('Failed to load category children.');

      const payload = (await response.json()) as HeaderCategoryChildrenResponse;
      if (!Array.isArray(payload.data)) throw new Error('Invalid category response.');

      return payload.data;
    },
  });
}
